import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { fetchStudyImages } from '../../features/upload/api';
import { routeBase, useCollection } from '../api/collections';
import { Button, TextLink } from '../ui/Button';
import { IconAlertCircle, IconArrowRight, IconCheck, IconSpinnerArc } from '../ui/icons';
import { Notice } from '../ui/Controls';
import { Breadcrumb } from './UploadStudyPage';
import { POLL_MS, dismissUpload, importDone, importProgress, importRowOf, importRowOfImage, sessionForStudy, sessionRoute, useUpload, type ImportRow } from './store';

/**
 * Import progress (`is.importing`) for one study: the 4×4 tile mosaic, title
 * and subtitle, the progress bar, the per-image list, and the leave-or-open
 * actions.
 *
 * The images are the ones this tab's upload session added to the study
 * (their transfers included), kept current by the session's own poll. With
 * no session for the study (another tab's upload, or one left) it is the
 * study's images as the server lists them, polled here.
 */
// One word and a percentage per image (decided 2026-09-10). Which stage an
// image is in (conversion, indexing) matters only to an operator, and the
// admin dashboard shows it; the percentage is the same one it shows.
const TILE_COLORS = ['linear-gradient(135deg,#efc4d3,#c67f9b 50%,#8a5375)', 'linear-gradient(135deg,#dcb896,#b07a4d 55%,#6e4426)', 'linear-gradient(135deg,#e9c6e0,#b982b8 55%,#6f4a7d)', 'linear-gradient(135deg,#bcd4e6,#7a9fc2 55%,#3f5f86)'];

export function ImportingPage() {
  const { publicId, studyId } = useParams<{ publicId: string; studyId: string }>();
  const nav = useNavigate();
  const { current } = useSelectedWorkspace();
  const { data: col } = useCollection(publicId);
  const mine = useUpload((st) => sessionForStudy(st.sessions, studyId));
  const listed = useQuery({
    queryKey: ['study-drafts', studyId],
    queryFn: () => fetchStudyImages(studyId!),
    enabled: !mine && !!studyId,
    refetchInterval: (q) => (q.state.data && importDone(q.state.data.map(importRowOfImage)) ? false : POLL_MS),
  });
  const back = () => nav(`${routeBase()}/collections/${publicId}`);

  const rows: ImportRow[] = mine ? mine.files.map(importRowOf) : (listed.data ?? []).map(importRowOfImage);
  // Seen to the end here: the top bar lets it go once the person leaves the
  // page (not at once, which would pull the session out from under it). Kept
  // per study: the dropdown moves this page from one study to another, and
  // the one left is judged by its own rows, not the next one's.
  const finished = useRef(new Map<string, boolean>());
  if (studyId) finished.current.set(studyId, importDone(rows));
  useEffect(() => () => {
    if (studyId && finished.current.get(studyId)) dismissUpload(studyId);
  }, [studyId]);
  if (!mine && !rows.length) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', background: '#fafaf9', fontSize: 13.5, color: '#8a8a8f' }}>
        {listed.isPending && !listed.isError ? null : (
          <span>
            This upload is no longer tracked here. <a href="#" onClick={(e) => { e.preventDefault(); back(); }} style={{ marginLeft: 6 }}>Back to the collection</a>
          </span>
        )}
      </div>
    );
  }
  // Where the details are given: this session's files page, or the study's
  // upload page, which picks up its unfinished images.
  const filesRoute = mine ? sessionRoute(routeBase(), mine) : `${routeBase()}/collections/${publicId}/upload/files?study=${encodeURIComponent(studyId ?? '')}`;
  const n = rows.length;
  const done = importDone(rows);
  const progress = importProgress(rows);
  const errs = rows.filter((r) => r.state === 'failed' || r.state === 'upload-failed');
  const needs = rows.filter((r) => r.state === 'needs-details' || r.state === 'select');
  const published = rows.filter((r) => r.state === 'published').length;
  const hasErrors = done && errs.length > 0;
  const isNew = mine?.mode === 'new';
  const label = mine?.label ?? '';
  const cur = rows.find((r) => r.state === 'uploading' || r.state === 'importing');
  const tiles = Array.from({ length: 16 }, (_, i) => {
    const lit = done || i < Math.round((progress / 100) * 16);
    const r = rows[i % n];
    return { bg: lit ? (r?.state === 'failed' ? '#f3c9c5' : TILE_COLORS[i % TILE_COLORS.length]) : '#e6e6e3', opacity: lit ? 1 : 0.6 };
  });
  const title = done
    ? hasErrors
      ? `Imported with ${errs.length} error${errs.length > 1 ? 's' : ''}`
      : isNew
        ? `${label} imported`
        : `${n} image${n > 1 ? 's' : ''} imported`
    : isNew
      ? `Importing ${label}`
      : `Importing ${n} image${n > 1 ? 's' : ''}`;
  const subtitle = done
    ? hasErrors
      ? `${n - errs.length} of ${n} images were imported. Re-export the failed file and upload it again.`
      : 'Every image is imported, ready to view and share.'
    : needs.length && !cur
      ? `${needs.length === 1 ? 'One image needs' : `${needs.length} images need`} your attention before ${needs.length === 1 ? 'it' : 'they'} can be shared. Unfinished images are removed after 24 hours.`
      : 'Your images are being imported. Large slides can take a few minutes.';
  const stepLabel = done ? 'Imported' : cur ? `${cur.state === 'uploading' ? 'Uploading' : 'Importing'} · ${cur.name}` : needs.length ? 'Needs details' : 'Importing';
  const rowStatus = (r: ImportRow) => {
    switch (r.state) {
      case 'uploading':
        return `Uploading · ${r.percent}%`;
      case 'select':
        return r.several ? 'Select its files again' : 'Select the file again';
      case 'upload-failed':
        return 'Upload failed';
      case 'importing':
        return `Importing · ${r.percent}%`;
      case 'needs-details':
        return 'Needs details';
      case 'published':
        return 'Imported';
      case 'failed':
        return 'Could not be imported';
    }
  };
  const rowColor = (r: ImportRow) =>
    r.state === 'failed' || r.state === 'upload-failed' ? '#d9342b' : r.state === 'published' ? '#2f6b3a' : r.state === 'needs-details' || r.state === 'select' ? '#9a6400' : r === cur ? '#0f0f10' : '#8a8a8f';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fafaf9' }}>
      <div className="tr-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px 0' }}>
        <Breadcrumb wsName={current?.name ?? ''} locationName={mine?.locationName || col?.name || ''} onBack={back} last="Importing" />
      </div>
      <div className="tr-page" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px 28px 60px' }}>
        <div style={{ width: '100%', maxWidth: 640, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28, textAlign: 'center' }}>
          <div style={{ position: 'relative', width: 216, height: 216 }}>
            <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'repeat(4, 1fr)', gap: 6 }}>
              {tiles.map((t, i) => (
                <div key={i} style={{ borderRadius: 6, background: t.bg, opacity: t.opacity, transition: 'opacity .5s ease, background .5s ease' }} />
              ))}
            </div>
            {!done ? (
              <div style={{ position: 'absolute', left: '50%', top: '50%', width: 64, height: 64, margin: '-32px 0 0 -32px', borderRadius: '50%', background: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,.14)', display: 'grid', placeItems: 'center' }}>
                <IconSpinnerArc size={26} color="#0f0f10" />
              </div>
            ) : (
              <div style={{ position: 'absolute', left: '50%', top: '50%', width: 64, height: 64, margin: '-32px 0 0 -32px', borderRadius: '50%', background: hasErrors ? '#d9342b' : '#0f0f10', boxShadow: '0 6px 24px rgba(0,0,0,.2)', display: 'grid', placeItems: 'center' }}>
                <IconCheck size={28} sw={2.6} color="#fff" />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-.02em' }}>{title}</h1>
            <p style={{ margin: 0, fontSize: 14, color: '#6b6b70', lineHeight: 1.5, textWrap: 'pretty' }}>{subtitle}</p>
          </div>
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#6b6b70' }}>
              <span>{stepLabel}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{progress}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: '#ececea', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, borderRadius: 3, background: '#0f0f10', transition: 'width .4s ease' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #ebebea', borderRadius: 10, background: '#fff', overflow: 'hidden', textAlign: 'left', marginTop: 6 }}>
              {rows.map((r, i) => (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '28px minmax(0,1fr) auto', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f0f0ee' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: TILE_COLORS[i % TILE_COLORS.length] }} />
                  <span className="tr-ellipsis" style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</span>
                  <span style={{ fontSize: 12, color: rowColor(r), whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {r.state === 'failed' || r.state === 'upload-failed' ? <IconAlertCircle size={12} sw={2.2} color="#d9342b" /> : null}
                    {rowStatus(r)}
                    {r.state === 'needs-details' || r.state === 'select' || r.state === 'upload-failed' ? (
                      <TextLink onClick={() => nav(filesRoute)} fontSize={12} underline hover="none" style={{ marginLeft: 4 }}>{r.state === 'needs-details' ? 'Add details' : 'Open'}</TextLink>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {!done ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13.5, color: '#6b6b70' }}>You can leave this screen. The import keeps running and won't be affected.</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Button variant="outlined" size={36} px={14} onClick={back}>Return to collection</Button>
                {needs.length ? (
                  <Button variant="primary" size={36} onClick={() => nav(filesRoute)}>Add details</Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {hasErrors ? (
            <Notice tone="danger" style={{ width: '100%', textAlign: 'left' }}>
              <span style={{ flex: 1 }}>{errs.length === 1 ? `${errs[0].name} could not be imported. ${errs[0].error ?? ''}` : `${errs.length} images could not be imported.`}</span>
            </Notice>
          ) : null}
          {done ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button variant="outlined" size={36} px={14} onClick={back}>Return to collection</Button>
              {/* Only when there is something to open: a study whose every image failed shows nothing. */}
              {published ? (
                <Button variant="primary" size={36} iconRight={<IconArrowRight size={14} />} onClick={() => nav(`${routeBase()}/studies/${encodeURIComponent(studyId!)}`)}>
                  Open study
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
